import {LogLevel, Occupancy, Platform} from "@variocube/app-api";
import {findBoxByNumber} from "@variocube/app-common";
import {BoxAccess, MessageView, PendingView, useAsync, usePlatform, usePreventDeactivate} from "@variocube/cube-ui";
import * as React from "react";
import {Fragment, useCallback} from "react";
import {OrderOccupancyContent, Shops} from "./types";
import {getShops} from "./common";
import {logger} from "./logger";
import {useLocalization} from "../i18n";

const log = logger.sub("store");

interface StoreProps {
    occupancy: Occupancy;
    onDone: () => void;
}

export function StoreFlow({occupancy, onDone}: StoreProps) {
    usePreventDeactivate();
    const platform = usePlatform();
    const {t} = useLocalization();

    const {loading, error, result} = useAsync(async () => ({
        order: await getOccupancyOrder(platform, occupancy),
        box: await findBoxByNumber(platform, occupancy.boxNumber)
    }), [platform, occupancy]);

    const handleConfirm = useCallback(async () => {
        onDone();
        try {
            log.info("Updating occupancy", occupancy);
            const occupancyContent = occupancy.content as OrderOccupancyContent;
            occupancyContent.order.customerStoredDate = new Date().toISOString();
            await platform.updateOccupancy(occupancy.uuid, occupancyContent);
            await platform.log(LogLevel.Audit, "Stored", occupancy);
        }
        catch (error) {
            log.error("Error ending occupancy", error);
        }
    }, [occupancy, platform, onDone]);


    if (loading) {
        return <PendingView message={t("LoadingOrder")}/>;
    }
    else if (error) {
        return <MessageView message={`Error loading order: ${error}`}/>;
    }
    else if (result) {
        const {order, box} = result;
        if (order && box) {
            return (
                <Fragment>
                    <BoxAccess
                        boxes={box}
                        platform={platform}
                        onCancel={onDone}
                        onConfirm={handleConfirm}
                        autoConfirm
                        boxOpenTitle={t("StoreTitle")}
                        boxOpenMessage={t("StoreMessage", { boxNames: box.number })}
                        confirmTitle={t("StoreConfirmTitle")}
                        confirmMessage={t("StoreConfirmMessage", { boxNames: box.number })}
                        strings={t}
                    />
                </Fragment>
            );
        }
    }
    return null;
}

async function getOccupancyOrder(platform: Platform, occupancy: Occupancy) {
    const shops = await getShops(platform);
    if (shops) {
        const orderId = getOrderId(occupancy);
        return orderId && findOrder(shops, orderId);
    }
}

function findOrder(shops: Shops, orderId: number) {
    for (const shop of Object.values(shops)) {
        for (const order of shop.orders) {
            if (order.id == orderId) {
                return order;
            }
        }
    }
    return undefined;
}

function getOrderId(occupancy: Occupancy) {
    const content = occupancy.content;
    if (content) {
        const {order} = content as OrderOccupancyContent;
        if (order) {
            return order.id;
        }
    }
    return undefined;
}
